import { Bot, Zap } from "lucide-react";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

export type AutoBidRow = {
  id: string;
  maxAmount: number;
  createdAt: string;
  updatedAt: string;
  bidderUserId: string;
  bidderName: string;
  bidderEmail: string | null;
  bidderOrganizationId: string;
  bidderOrgName: string;
  bidderOrgSlug: string | null;
};

interface AuctionAutoBidsProps {
  autoBids: AutoBidRow[];
  /** Organization id currently holding the highest bid, if any. */
  leadingOrganizationId?: string | null;
}

export function AuctionAutoBids({ autoBids, leadingOrganizationId }: AuctionAutoBidsProps) {
  const sorted = [...autoBids].sort((a, b) => b.maxAmount - a.maxAmount);

  if (autoBids.length === 0) {
    return (
      <Card className="shadow-sm border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Bot className="h-4 w-4 text-muted-foreground" />
            Aktive autobud
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[11px] text-muted-foreground italic text-center py-4">
            Ingen aktive autobud
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Bot className="h-4 w-4 text-primary" />
          Aktive autobud
        </CardTitle>
        <Badge variant="muted" className="text-[10px] font-bold">
          {autoBids.length}
        </Badge>
      </CardHeader>
      <CardContent className="px-2 pb-2">
        <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
          {sorted.map((ab) => {
            const isLeading = leadingOrganizationId != null && ab.bidderOrganizationId === leadingOrganizationId;
            return (
              <div
                key={ab.id}
                className={`rounded-xl border p-3 space-y-1.5 transition-all hover:bg-muted/10 ${
                  isLeading
                    ? "border-primary/20 bg-primary/5 shadow-sm"
                    : "border-border/40 bg-muted/5 hover:border-border"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <p className="text-[13px] font-black text-primary flex items-center gap-1" style={{ fontFamily: "var(--font-display)" }}>
                        <Zap className="h-3 w-3" />
                        {ab.maxAmount.toLocaleString("nb-NO")} kr
                      </p>
                      {isLeading && (
                        <Badge variant="accent" className="h-4 px-1 text-[8px] font-black uppercase tracking-tighter">Leder</Badge>
                      )}
                    </div>
                    <p className="text-[10px] font-bold text-muted-foreground truncate uppercase tracking-tight">
                      {ab.bidderOrgName}
                    </p>
                    <p className="text-[9px] text-muted-foreground/80 font-medium truncate mt-0.5">
                      {ab.bidderName}
                    </p>
                  </div>

                  <div className="flex flex-col items-end gap-0.5">
                    <span className="text-[8px] font-bold uppercase tracking-tight text-muted-foreground/70">Maks</span>
                    <span className="text-[9px] font-medium text-muted-foreground whitespace-nowrap tabular-nums">
                      {new Date(ab.updatedAt).toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
